import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  CheckCircle2,
  CloudOff,
  Inbox,
  Loader2,
  LogIn,
  RefreshCw,
  Sparkles,
  XCircle,
} from "lucide-react";
import RequestInviteCard from "../components/requests/RequestInviteCard";
import RequestsChrome from "../components/requests/RequestsChrome";
import RequestsFilterNav from "../components/requests/RequestsFilterNav";
import { selectAppUser } from "../redux/appAuthSlice";
import { fetchAllRequests } from "../redux/requestsSlice";
import {
  buildRequestItems,
  countInboxItems,
  countOutboxItems,
  defaultRequestView,
  filterRequestItems,
  groupQueueItems,
} from "../utils/requestsHelpers";
import "../requests.css";

const Requests = () => {
  const { itemId: routeItemId } = useParams();
  const dispatch = useDispatch();
  const user = useSelector(selectAppUser);
  const userId = user?._id;
  const { userP2PBets, marketP2PBets, status, error } =
    useSelector((s) => s.requests);

  const [view, setView] = useState("inbox");
  const [search, setSearch] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState(null);
  const [viewInitialized, setViewInitialized] = useState(false);

  const allItems = useMemo(
    () =>
      buildRequestItems({
        userP2PBets,
        marketP2PBets,
        userId,
      }),
    [userP2PBets, marketP2PBets, userId]
  );

  const inboxCount = useMemo(() => countInboxItems(allItems), [allItems]);
  const outboxCount = useMemo(() => countOutboxItems(allItems), [allItems]);

  const viewCounts = useMemo(
    () => ({
      inbox: inboxCount,
      outbox: outboxCount,
      all: allItems.length,
    }),
    [inboxCount, outboxCount, allItems.length]
  );

  const filtered = useMemo(
    () => filterRequestItems(allItems, { view, search }),
    [allItems, view, search]
  );

  const feedGroups = useMemo(() => {
    if (view !== "all") return [{ id: "flat", label: "", items: filtered }];
    return groupQueueItems(filtered);
  }, [filtered, view]);

  const load = useCallback(() => {
    if (!userId) return Promise.resolve();
    return dispatch(fetchAllRequests(userId));
  }, [dispatch, userId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (viewInitialized || !allItems.length) return;
    setView(defaultRequestView(allItems));
    setViewInitialized(true);
  }, [allItems, viewInitialized]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), 4200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (!routeItemId) return undefined;
    const decoded = decodeURIComponent(routeItemId);
    if (decoded.startsWith("p2p-received-")) {
      setView("inbox");
    }
    const timer = window.setTimeout(() => {
      document
        .getElementById(`request-${decoded}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [routeItemId, filtered]);

  const onNotify = (type, title, detail) => setToast({ type, title, detail });

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await load();
    } finally {
      setRefreshing(false);
    }
  };

  if (!userId) {
    return (
      <div className="req-page">
        <div className="req-state">
          <LogIn size={32} />
          <h1>Sign in to manage P2P requests</h1>
          <Link to="/app/login" className="req-primary-btn">
            Go to login
          </Link>
        </div>
      </div>
    );
  }

  if (status === "loading" && !allItems.length) {
    return (
      <div className="req-page">
        <div className="req-state">
          <Loader2 size={22} className="animate-spin" />
          <p>Loading your P2P requests…</p>
        </div>
      </div>
    );
  }

  if (status === "failed" && !allItems.length) {
    return (
      <div className="req-page">
        <div className="req-state">
          <CloudOff size={32} />
          <p>{String(error || "Could not load requests.")}</p>
          <button type="button" className="req-primary-btn" onClick={onRefresh}>
            <RefreshCw size={13} />
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="req-page">
      <div className="req-desk">
        <div className="req-desk-chrome">
          <RequestsChrome
            replyCount={inboxCount}
            outboxCount={outboxCount}
            totalCount={allItems.length}
            search={search}
            onSearchChange={setSearch}
            refreshing={refreshing}
            onRefresh={onRefresh}
          />
        </div>

        <div className="req-desk-body req-desk-command">
          <RequestsFilterNav
            view={view}
            onViewChange={setView}
            viewCounts={viewCounts}
            replyCount={inboxCount}
            resultCount={filtered.length}
          />

          <div className="req-feed" aria-label="Your P2P requests">
            <div className="req-feed-inner">
              {filtered.length ? (
                feedGroups.map((group) => (
                  <section key={group.id} className="req-feed-section">
                    {group.label ? (
                      <h2 className="req-feed-section-label">{group.label}</h2>
                    ) : null}
                    <div className="req-feed-list">
                      {group.items.map((item) => (
                        <RequestInviteCard
                          key={item.id}
                          item={item}
                          userId={userId}
                          onNotify={onNotify}
                          onRefresh={onRefresh}
                        />
                      ))}
                    </div>
                  </section>
                ))
              ) : (
                <div className="req-feed-empty">
                  {view === "inbox" && inboxCount === 0 ? (
                    <>
                      <Sparkles size={28} strokeWidth={1.25} />
                      <h2>Inbox clear</h2>
                      <p>No pending P2P invites. New challenges sent to you will show up here.</p>
                    </>
                  ) : (
                    <>
                      <Inbox size={28} strokeWidth={1.25} />
                      <h2>No P2P requests here</h2>
                      <p>
                        {search.trim()
                          ? "Try a different search term."
                          : "When someone invites you to a P2P challenge, it will appear in this list."}
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {toast ? (
        <div className={`req-toast req-toast-${toast.type}`} role="status">
          {toast.type === "success" ? <CheckCircle2 size={15} /> : <XCircle size={15} />}
          <div>
            <strong>{toast.title}</strong>
            {toast.detail ? <span>{toast.detail}</span> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Requests;
